import {PointCloud} from './point-cloud';
import {EncompassPointCloudNode} from "./encompass-pointcloud-node";
import {readFileJSON} from "../file-system-loader";
import LocalScene from '../projections';
import { Vector3 } from 'three';
import { WorkerPool } from '../worker';

export class EncompassPointCloud extends PointCloud {
  public workerPool: WorkerPool;
  private structure = null;
  public nodeCacheSize = 200;
  private cloudInfo: any;
  private numberOfPoints: any;
  private _shift: any;

  constructor(parent) {
    super(parent);

    const workerURL = "workers/pointcloud-decoder-node.js";
    let workerPath = `${process.env.PUBLIC_URL}/${workerURL}`;
    this.workerPool = new WorkerPool(workerPath);
  }

  get dataDir() {
    return this.structure.path;
  }

  get precision() {
    return this.cloudInfo.precision;
  }

  get folderDepth() {
    return this.cloudInfo.folderDepth;
  }

  get spacing() {
    return this.cloudInfo.spacing;
  }

  get isRemoteFile() {
    return this.structure.remote;
  }

  get fromJSON() {
    return this.structure.fromJSON;
  }

  get size() {
    return this.root.size;
  }

  get shift() {
    return this._shift;
  }

  set shift(value) {
    this._shift = value;
  }

  /** @private */
  destroyWorker() {
    this.workerPool.terminateWorkers();
  }

  getDataPath(node) {
    let dataDir = this.dataDir;
    let folderDepth = this.folderDepth;

    if (node === "r") {
      return dataDir;
    }

    let name = node.slice(1);
    if ((folderDepth) && (name.length >= folderDepth)) {
      let dirs = name.match(new RegExp('.{' + folderDepth + '}', 'g'));
      dataDir = `${dataDir}/${dirs.join("/")}`;
    }

    return dataDir;
  }

  setBoundingBox(boundingBox) {
    if (!boundingBox) {
      return;
    }

    this._tightBoundingBox = boundingBox;
  }

  loadNodes(structure) {
    this.structure = structure;

    // Add pointcloud and set parameters
    this.pointclouds.addPointCloud(this);
    this.setName(structure.name);
    this.setVisibility(structure.visible);
    this.setIdentifier(structure.identifier);

    let path = `${structure.path}/r.idx`;

    // Read index data
    readFileJSON(path, this.isRemoteFile)
      .then(data => {
        // Decode our root info
        this.decodeIndexData(data);
      })
      .catch(() => {
        this.fileMissing = true;
        console.error(`Error loading point cloud: ${structure.path}`);
        this.viewer.showMessage(`Error loading point cloud: ${structure.name}`, {
          variant: "error"
        });
      });
  }

  decodeIndexData(data) {
    // Decode our root index file and store info about our local projection
    let cloudInfo = {
      spacing: data.hasOwnProperty('spacing') ? data.spacing : Math.sqrt(2) * data.size / 250.0,
      folderDepth: data.hasOwnProperty('hierarchy_step_size') ? data.hierarchy_step_size : null,
      precision: data.hasOwnProperty('precision') ? data.precision : 0.005,
      offset: new Vector3(...data.point_offset),
      scale: data.point_scale
    };

    let offset = cloudInfo.offset;
    let availableNodes = data.hasOwnProperty('node_structure') ? data.node_structure : null;
    let numberOfPoints = data.hasOwnProperty('total_points') ? data.total_points : null;

    // Get our tight bounding box if available
    let tightBoundingBox = null;
    if ("tight_bounding_box" in data) {
      let box = data.tight_bounding_box;
      tightBoundingBox = {
        min: new Vector3(...box.min).sub(offset),
        max: new Vector3(...box.max).sub(offset)
      };
    }

    // Setting some cloud metadata
    this.numberOfPoints = numberOfPoints;
    this.cloudInfo = cloudInfo;

    // Generate cloud root node
    let nodeSize = parseFloat(data.size);
    let nodeCenter = new Vector3(...data.center)
      .sub(offset);

    this.shift = LocalScene.offsetToScene(offset);
    this.root = new EncompassPointCloudNode(this, null, "r", 1,
      nodeCenter, nodeSize);

    this.buildTreeLegacy(availableNodes);
    this.setBoundingBox(tightBoundingBox);
    this.pointclouds.updateMinimapExtent();

    if (!this.fromJSON) {
      let bbox = this.tightBoundingBox;
      this.pointclouds.zoomToExtent(bbox, false, false);
    }
  }

  /** Legacy code for older pointclouds. Should be removed eventually */
  buildTreeLegacy(availableNodes) {
    // Old file format. Fallback method will add child nodes as needed
    if (!availableNodes) {
      return;
    }

    // Too slow, dont bother.
    // NOTE: remove completely soon. very slow past this cutoff, but
    // causes weird artifacts if removed completely
    let keys = Object.keys(availableNodes);
    let cutoff = 50000;
    if (keys.length > cutoff) {
      return;
    }

    // Make sure our root node was passed in
    let rootNodeExists = "r" in availableNodes;
    if (!rootNodeExists) {
      return;
    }

    let totalAdded = 1;
    let stack = [this.root];

    while (stack.length > 0) {
      let object = stack.shift();
      object.clearChildNodes();
      object.numPoints = availableNodes[object.name];

      for (var i = 0; i < 8; i++) {
        let childName = object.getChildNameAtIndex(i);
        let childExists = childName in availableNodes;

        if (childExists) {
          // This node exists in our structure, so we push to the child array
          // of the parent node, and append to our stack
          let child = object.getChildAtIndex(i);
          object.addChildNode(child);
          stack.push(child);
          totalAdded++;
        }
      }
    }

    console.log(`Point cloud hierarchy loaded. ${totalAdded} nodes found`);
  }
}
